import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import api from "utils/axiosInstance";

const TAILWIND_SCRIPT_ATTR = "data-tailwind-cdn";
const TAILWIND_SCRIPT_SELECTOR =
  'script[data-tailwind-cdn="true"], script#tailwind-cdn';

const TailwindInjector = ({ children }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const removeTailwindNodes = () => {
      document
        .querySelectorAll(TAILWIND_SCRIPT_SELECTOR)
        .forEach((node) => node.remove());
      document.querySelectorAll("style").forEach((node) => {
        if (node.textContent && node.textContent.includes("--tw-")) {
          node.remove();
        }
      });
      document.querySelectorAll("link[rel='stylesheet']").forEach((node) => {
        if (node.href && node.href.includes("tailwind")) {
          node.remove();
        }
      });
      delete window.tailwind;
    };

    const existingScript = document.querySelector(TAILWIND_SCRIPT_SELECTOR);

    if (window.tailwind && existingScript) {
      setLoaded(true);
      return () => {
        removeTailwindNodes();
      };
    }

    if (existingScript) {
      const handleLoad = () => setLoaded(true);
      existingScript.addEventListener("load", handleLoad);
      return () => {
        existingScript.removeEventListener("load", handleLoad);
        removeTailwindNodes();
      };
    }

    const script = document.createElement("script");
    script.id = "tailwind-cdn";
    script.src = "https://cdn.tailwindcss.com";
    script.async = true;
    script.setAttribute(TAILWIND_SCRIPT_ATTR, "true");
    const handleLoad = () => {
      setLoaded(true);
    };
    script.addEventListener("load", handleLoad);
    document.head.appendChild(script);

    return () => {
      script.removeEventListener("load", handleLoad);
      removeTailwindNodes();
    };
  }, []);

  if (!loaded)
    return (
      <div className="flex items-center justify-center min-h-screen font-sans">
        Loading Styles...
      </div>
    );
  return children;
};

const ArithmeticTask = () => {
  const TASK_DURATION_MS = 3 * 60 * 1000;
  //const TASK_DURATION_MS = 10000;
  const TRIAL_TIMEOUT_MS = 10 * 1000;

  const [screen, setScreen] = useState("welcome");
  const [subjectId, setSubjectId] = useState();
  const [visitType, setVisitType] = useState("");
  const [startingNumber, setStartingNumber] = useState(null);
  const [currentCorrectNumber, setCurrentCorrectNumber] = useState(0);
  const [userAnswer, setUserAnswer] = useState("");
  const [incorrectStreak, setIncorrectStreak] = useState(0);
  const [trialData, setTrialData] = useState([]);
  const [feedback, setFeedback] = useState({ text: "", type: "" });
  const [progress, setProgress] = useState(100);
  const [stressRating, setStressRating] = useState(null);
  const [isTaskActive, setIsTaskActive] = useState(false);
  const [showInput, setShowInput] = useState(false);

  const trialStartTime = useRef(0);
  const trialTimeoutRef = useRef(null);
  const taskStartTimeRef = useRef(null);
  const trialCounter = useRef(0);
  const inputRef = useRef(null);
  const currentCorrectNumberRef = useRef(null);
  const navigate = useNavigate();

  const generateRandomNumber = (min, max) =>
    Math.floor(Math.random() * (max - min + 1)) + min;

  const showFeedback = (text, type, duration = 1000) => {
    setFeedback({ text, type });
    setTimeout(() => setFeedback({ text: "", type: "" }), duration);
  };

  useEffect(() => {
    currentCorrectNumberRef.current = currentCorrectNumber;
  }, [currentCorrectNumber]);

  const downloadCSV = (data, filename) => {
    if (!data.length) return;
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(","),
      ...data.map((row) =>
        headers
          .map((h) => {
            let val = String(row[h] ?? "");
            return val.includes(",") ? `"${val}"` : val;
          })
          .join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  const handleStartSubmit = () => {
    if (!subjectId || !visitType) {
      showFeedback("Please fill in all fields", "text-red-500", 2000);
      return;
    }
    setScreen("instructions");
  };

  useEffect(() => {
    const handleKeydown = (e) => {
      if (screen === "instructions" && e.code === "Space") {
        const elem = document.documentElement;
        if (elem.requestFullscreen) elem.requestFullscreen().catch(() => {});
        startActualTask();
      }
    };
    window.addEventListener("keydown", handleKeydown);
    return () => window.removeEventListener("keydown", handleKeydown);
  }, [screen]);

  const startActualTask = () => {
    setScreen("task");
    const startNum = generateRandomNumber(6000, 9000);
    setStartingNumber(startNum);
    setCurrentCorrectNumber(startNum);
    // console.log(currentCorrectNumber);
    taskStartTimeRef.current = Date.now();
    setIsTaskActive(true);
    setTimeout(() => {
      setStartingNumber(null);
      nextTrial();
    }, 4000);
  };

  const nextTrial = () => {
    setShowInput(true);
    setUserAnswer("");
    trialStartTime.current = Date.now();
    trialCounter.current++;
    trialTimeoutRef.current = setTimeout(handleMissedAttempt, TRIAL_TIMEOUT_MS);
  };

  useEffect(() => {
    if (showInput && inputRef.current) inputRef.current.focus();
  }, [showInput]);

  useEffect(() => {
    let interval;
    if (isTaskActive) {
      interval = setInterval(() => {
        const elapsed = Date.now() - taskStartTimeRef.current;
        const remaining = Math.max(0, 100 - (elapsed / TASK_DURATION_MS) * 100);
        setProgress(remaining);
        if (elapsed >= TASK_DURATION_MS) {
          setIsTaskActive(false);
          clearTimeout(trialTimeoutRef.current);
          if (document.fullscreenElement)
            document.exitFullscreen().catch(() => {});
          setScreen("endTask");
          setTimeout(() => setScreen("stressRating"), 1500);
        }
      }, 100);
    }
    return () => clearInterval(interval);
  }, [isTaskActive]);

  const handleInputSubmit = (e) => {
    if (e.key === "Enter") {
      clearTimeout(trialTimeoutRef.current);
      const rt = Date.now() - trialStartTime.current;
      const val = parseInt(userAnswer, 10);
      const expected = currentCorrectNumber - 7;
      setShowInput(false);
      const isCorrect = val === expected;
      const result = isNaN(val)
        ? "Invalid"
        : isCorrect
          ? "Correct"
          : "Incorrect";

      setTrialData((prev) => [
        ...prev,
        {
          subject_id: subjectId,
          visit_type: visitType,
          trial_date: new Date().toISOString().split("T")[0],
          trial_number: trialCounter.current,
          user_input: String(val),
          expected_answer: expected,
          result,
          reaction_time_ms: rt,
        },
      ]);

      if (isCorrect) {
        setCurrentCorrectNumber(val);
        setIncorrectStreak(0);
        showFeedback("Correct!", "text-green-500");
        setTimeout(nextTrial, 1000);
      } else {
        const streak = incorrectStreak + 1;
        setIncorrectStreak(streak);
        if (streak >= 3) {
          showFeedback(
            `Incorrect. The answer was ${expected}`,
            "text-blue-500",
            3000,
          );
          setCurrentCorrectNumber(expected);
          setIncorrectStreak(0);
          setTimeout(nextTrial, 3000);
        } else {
          showFeedback("Incorrect, try again", "text-red-500");
          setTimeout(nextTrial, 1000);
        }
      }
    }
  };

  const handleMissedAttempt = () => {
    setShowInput(false);
    const expected = currentCorrectNumber - 7;
    const streak = incorrectStreak + 1;
    setIncorrectStreak(streak);
    if (streak >= 3) {
      showFeedback(
        `Missed. The correct answer was ${currentCorrectNumberRef.current - 7}`,
        "info",
        3000,
      );

      setCurrentCorrectNumber(expected);
      setIncorrectStreak(0);
    } else {
      showFeedback(
        `Missed attempt. Last correct answer was ${currentCorrectNumberRef.current}`,
        "info",
        3000,
      );
    }
    setTimeout(nextTrial, 3000);
  };

  const summary = (() => {
    const totalTrials = trialData.length;
    const correctTrials = trialData.filter((t) => t.result === "Correct");
    const incorrectTrials = trialData.filter((t) => t.result !== "Correct");
    const totalCorrect = correctTrials.length;
    const overallAccuracy =
      totalTrials > 0 ? (totalCorrect / totalTrials) * 100 : 0;

    const sumCorrectRt = correctTrials.reduce(
      (sum, t) => sum + t.reaction_time_ms,
      0,
    );
    const correctMeanRt = totalCorrect > 0 ? sumCorrectRt / totalCorrect : 0;

    const sumIncorrectRt = incorrectTrials.reduce(
      (sum, t) => sum + t.reaction_time_ms,
      0,
    );
    const incorrectMeanRt =
      incorrectTrials.length > 0 ? sumIncorrectRt / incorrectTrials.length : 0;

    // const avgRt = correct.length
    //   ? (
    //       correct.reduce((s, t) => s + t.reaction_time_ms, 0) / correct.length
    //     ).toFixed(0)
    //   : 0;
    return {
      accuracy_percentage: `${overallAccuracy.toFixed(1)}`,
      total_correct: totalCorrect,
      total_trials: totalTrials,
      correct_mean_rt_ms: `${correctMeanRt.toFixed(0)}`,
      incorrect_mean_rt_ms: `${incorrectMeanRt.toFixed(0)}`,
      stress_rating: stressRating,
    };
  })();

  const sendDataToServer = async () => {
    const payload = {
      metadata: {
        subject_id: subjectId,
        visit_type: visitType,
        test_type: "Arithmetic",
        date: new Date().toISOString(),
      },
      summary: summary,
      trials: trialData,
    };

    try {
      //const response = await api.post("/Task/Arithmetic/Submit", payload);

      const response = await api({
        method: "post",
        url: "/Task/Arithmetic/Submit",
        data: payload,
        headers: {
          "Content-Type": "application/json",
        },
      });
      console.log("Data saved successfully:", response.data);
      alert("Data synced to server");
    } catch (error) {
      if (error.response) {
        const message = error.response.data;
        if (message) {
          alert(message);
        } else {
          alert("Server error");
        }
      } else {
        alert("Network error. Please check your connection.");
      }
    }
  };

  const handleSaveData = () => {
    const dateStr = new Date().toISOString().split("T")[0].replace(/-/g, "");
    const filename = `${subjectId}_Arithmetic_${dateStr}_${visitType}`;
    downloadCSV(trialData, `${filename}_Trials.csv`);
    downloadCSV([summary], `${filename}_Summary.csv`);
    sendDataToServer();
  };

  return (
    <TailwindInjector>
      <div className="min-h-screen bg-white text-gray-800 flex flex-col items-center justify-center p-6 font-sans select-none">
        {screen === "welcome" && (
          <div className="w-full max-w-sm space-y-8 text-center animate-in fade-in duration-500">
            <h1 className="text-3xl font-bold">Arithmetic Task</h1>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Subject ID"
                value={subjectId}
                onChange={(e) => setSubjectId(e.target.value)}
                className="w-full p-3 border rounded bg-gray-50 outline-none focus:ring-2 focus:ring-blue-400"
              />
              <select
                value={visitType}
                onChange={(e) => setVisitType(e.target.value)}
                className="w-full p-3 border rounded bg-gray-50 outline-none"
              >
                <option value="">-- Select Visit --</option>
                <option value="Baseline">Baseline</option>
                <option value="Post-training">Post-training</option>
                <option value="Follow-up">Follow-up</option>
              </select>
              <button
                onClick={handleStartSubmit}
                className="w-full bg-blue-600 text-white p-3 rounded font-bold hover:bg-blue-700 transition"
              >
                Start Task
              </button>
            </div>
            <p className={`h-4 font-medium ${feedback.type}`}>
              {feedback.text}
            </p>
          </div>
        )}

        {screen === "instructions" && (
          <div id="instructions-screen" className="screen">
            <div className="text-left space-y-4 max-w-2xl">
              <h2 className="text-2xl font-bold text-center">Instructions</h2>
              <p>
                You will see a <b>four-digit number</b> on the screen.
              </p>
              <p>
                Your task is to <b>subtract 7</b> from that number in your head,
                then type the result using your keyboard and press <b>Enter</b>.
              </p>
              <p>
                The screen will then go blank, and you{" "}
                <b>won’t see the number you just typed again</b>.
              </p>
              <p>
                Keep <b>subtracting 7 from your previous answer</b> and enter
                each new result. You’ll do this for the <b>entire duration</b>{" "}
                of the task—always subtracting 7 from your last response.
              </p>
              <p>
                If your answer is <b>correct</b>, you’ll move on. If it’s{" "}
                <b>wrong</b>, you’ll be asked to try again. If you get it wrong{" "}
                <b>three times in a row</b>, the{" "}
                <b>correct number will appear</b> so you can get back on track.
                Subtract 7 from that new number and keep going.
              </p>
              <p className="text-center font-bold pt-4 text-lg">
                The task will now enter full-screen mode.
              </p>
              <p className="text-center font-bold pt-2 text-lg">
                Press the <b>SPACE bar</b> when you are ready to begin.
              </p>
            </div>
          </div>
        )}

        {screen === "task" && (
          <div className="w-full max-w-2xl flex flex-col items-center">
            <div className="w-full bg-gray-200 h-3 rounded-full mb-20 overflow-hidden">
              <div
                className="bg-blue-500 h-full transition-all duration-150"
                style={{ width: `${progress}%` }}
              />
            </div>
            {startingNumber && (
              <div className="text-9xl font-bold tracking-tighter">
                {startingNumber}
              </div>
            )}
            <div className="h-24 flex items-center justify-center mb-10 text-4xl font-bold">
              <span className={feedback.type}>{feedback.text}</span>
            </div>
            {showInput && (
              <div className="text-center">
                <p className="text-2xl text-gray-400 mb-4 font-medium uppercase tracking-widest">
                  Subtract 7 and enter the number
                </p>
                <input
                  ref={inputRef}
                  type="number"
                  value={userAnswer}
                  onChange={(e) => setUserAnswer(e.target.value)}
                  onKeyDown={handleInputSubmit}
                  className="text-7xl font-bold text-center border-b-8 border-blue-500 w-80 focus:outline-none bg-transparent"
                />
              </div>
            )}
          </div>
        )}

        {screen === "endTask" && (
          <h1 className="text-5xl font-bold tracking-tight">Task Finished.</h1>
        )}

        {screen === "stressRating" && (
          <div className="text-center space-y-8 animate-in zoom-in duration-300">
            <h2 className="text-2xl font-bold">How stressful was this?</h2>
            <div className="flex gap-2 justify-center flex-wrap">
              {[...Array(11).keys()].map((n) => (
                <button
                  key={n}
                  onClick={() => setStressRating(n)}
                  className={`w-12 h-12 rounded-full border-2 font-bold transition-all ${stressRating === n ? "bg-blue-600 text-white border-blue-600" : "border-gray-300 hover:border-blue-400"}`}
                >
                  {n}
                </button>
              ))}
            </div>
            <button
              onClick={() => {
                if (stressRating !== null) {
                  setScreen("summary");
                  handleSaveData();
                }
              }}
              className="bg-blue-600 text-white px-12 py-3 rounded-lg font-bold text-lg hover:shadow-lg transition"
            >
              Submit
            </button>
          </div>
        )}

        {screen === "summary" && (
          <div className="w-full max-w-sm space-y-6">
            <h1 className="text-4xl font-extrabold text-center">Summary</h1>
            <div className="bg-gray-50 border p-6 rounded-2xl space-y-4 shadow-sm">
              <div className="flex justify-between">
                <span>Accuracy:</span>
                <b>{summary.accuracy_percentage}%</b>
              </div>
              <div className="flex justify-between">
                <span>Correct:</span>
                <b>{summary.total_correct}</b>
              </div>
              <div className="flex justify-between">
                <span>Total Trials:</span>
                <b>{summary.total_trials}</b>
              </div>
              <div className="flex justify-between">
                <span>Correct Mean RT:</span>
                <b>{summary.correct_mean_rt_ms} ms</b>
              </div>
              <div className="flex justify-between">
                <span>Incorrect Mean RT:</span>
                <b>{summary.incorrect_mean_rt_ms} ms</b>
              </div>
              <div className="flex justify-between">
                <span>Stress:</span>
                <b>{summary.stress_rating}/10</b>
              </div>
            </div>
            <button
              onClick={handleSaveData}
              className="w-full bg-green-600 text-white p-4 rounded-xl font-bold shadow-md hover:bg-green-700 transition"
            >
              Save Data
            </button>
            <button
              onClick={() => (window.location.href = "/dashboard/subject")}
              className="w-full bg-red-600 text-white p-4 rounded-xl font-bold shadow-md hover:bg-green-700 transition"
            >
              Go Back
            </button>
          </div>
        )}
      </div>
    </TailwindInjector>
  );
};

export default ArithmeticTask;
